import type {
  Attachment,
  InvitationStatus,
  PaymentScheduleEntry,
  PaymentType,
  Project,
} from './project'
import type { PreUser, User } from './user'
import type { Contact } from './user'

// Snapshot data (immutable historical info)
export interface PartySnapshot {
  first_name: string
  last_name: string
  email: string
  phone?: string | null
  company_name?: string | null
  company_address?: string | null
}

export type TruckType =
  | 'standard'
  | 'super'
  | 'winter'
  | 'transfer'
  | 'side'
  | 'haul'
  | 'semi_end'
  | 'semi_bottom'

export const TRUCK_TYPE_OPTIONS = [
  { value: 'standard', label: 'Standard Dump Truck' },
  { value: 'super', label: 'Super Dump Truck' },
  { value: 'winter', label: 'Winter Weather Dump Truck' },
  { value: 'transfer', label: 'Transfer Dump Truck' },
  { value: 'side', label: 'Side Dump Truck' },
  { value: 'haul', label: 'Haul Dump Truck' },
  { value: 'semi_end', label: 'Semi-Truck Trailer End Dump Truck' },
  { value: 'semi_bottom', label: 'Semi-Truck Trailer Bottom Dump Truck' },
]

export const getTruckTypeLabel = (type: TruckType): string => {
  return (
    TRUCK_TYPE_OPTIONS.find((option) => option.value === type)?.label || type
  )
}

export enum WorkOrderStatus {
  DRAFT = 'DRAFT',
  NEW = 'NEW',
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  COMPLETE = 'COMPLETE',
}

export const getWorkOrderStatusLabel = (status: WorkOrderStatus): string => {
  switch (status) {
    case WorkOrderStatus.DRAFT:
      return 'Draft'
    case WorkOrderStatus.NEW:
      return 'New'
    case WorkOrderStatus.PENDING:
      return 'Pending'
    case WorkOrderStatus.ACCEPTED:
      return 'Accepted'
    case WorkOrderStatus.DECLINED:
      return 'Declined'
    case WorkOrderStatus.COMPLETE:
      return 'Complete'
    default:
      return status
  }
}

export interface WorkOrderStatusUpdateData {
  status: WorkOrderStatus
  decline_reason?: string
}

export interface DesignatedDriver {
  id?: number
  first_name: string
  last_name: string
  phone_number: string
  email_address: string
  note?: string
  order?: number
}

export interface WorkOrderAcceptData {
  designated_drivers: Omit<DesignatedDriver, 'id' | 'order'>[]
  number_of_trucks_accepted: number
}

export interface WorkOrder {
  id?: string
  label?: string
  project: Project | number // Can be Project object or just ID
  created_by: User
  created_date?: string
  updated_date?: string
  subhaulers?: User[]
  dispatchers?: User[]
  drivers?: User[]
  trucks_number: string
  truck_types: TruckType[]
  material: string
  volume: string
  rate_type?: string
  pay_rate: string
  // Payment schedule fields (carry-forward from project terms)
  payment_type?: PaymentType
  payment_schedule?: PaymentScheduleEntry[]
  total_scheduled_payment?: string | number
  total_scheduled_fees?: string | number
  pickup_address: string
  dropoff_address: string
  third_party_location?: string
  starting_date?: string
  completion_date?: string
  status?: WorkOrderStatus
  decline_reason?: string
  declined_by?: User
  declined_at?: string
  number_of_trucks_accepted?: number
  designated_drivers?: DesignatedDriver[]
  total_hours?: number
  total_loads?: number
  notes?: string
  status_interval?: string
  // New multi-party invitation acceptance fields
  invitation_status?: InvitationStatus
  total_owner_invites_sent?: number
  total_owner_invites_accepted?: number
  invitations?: WorkOrderInvite[]
  // Template recipient type for categorizing the dispatch
  template_recipient_type?: string
  // Contractor's Dispatcher info (prefilled from current user)
  contractor_dispatcher_first_name?: string
  contractor_dispatcher_last_name?: string
  contractor_dispatcher_phone?: string
  contractor_dispatcher_email?: string
  contractor_dispatcher_id?: number
  // Snapshot fields (immutable)
  created_by_first_name?: string
  created_by_last_name?: string
  created_by_email?: string
  created_by_company_name?: string
  created_by_company_address?: string
  declined_by_first_name?: string
  declined_by_last_name?: string
  declined_by_email?: string
  declined_by_company_name?: string
  accepted_by_first_name?: string
  accepted_by_last_name?: string
  accepted_by_email?: string
  accepted_by_company_name?: string
  // Role of who created/sent this dispatch
  created_by_role?:
    | 'BROKER'
    | 'CONTRACTOR'
    | 'SUB_HAULER'
    | 'THIRD_PARTY_BROKER'
    | 'THIRD_PARTY_CONTRACTOR'
  // Snapshot data from backend (resolved from project/workorder snapshots - immutable historical data)
  contractor_snapshot?: PartySnapshot
  broker_snapshot?: PartySnapshot
  sub_hauler_snapshot?: PartySnapshot
  // Attachments
  attachments?: Attachment[]
}

export interface DriverAssignmentResult {
  driver_id: number
  driver_name: string
  added_to_contacts: boolean
  already_in_contacts: boolean
  success: boolean
  error?: string
}

export interface DriverAssignmentResponse {
  success: boolean
  message: string
  results: DriverAssignmentResult[]
}

export interface DriverAssignmentRequest {
  driver_ids: number[]
}

export interface WorkOrderInvite {
  id: string
  workorder: WorkOrder
  email: string
  invited_by: User
  accepted: boolean
  created_date: string
  updated_date: string
  // New multi-party invitation acceptance fields
  can_accept?: boolean
  invited_user_type?: string
  // Alternative property paths for work order ID (backend may return different structures)
  workorder_id?: number | string
  work_order_id?: number | string
}

// Work Order Report Types
export interface WorkOrderReportUser {
  id: number
  first_name: string
  last_name: string
}

export interface WorkOrderReportTag {
  id: number
  label: string
  title: string
  status: string
  material: string
  destination_address: string
  assigned_user: WorkOrderReportUser
  total_hours: number
  total_loads: number
  start_date: string
  additional_expenses: Record<string, string>
  // New fields from updated backend schema
  truck_number?: string
  contractor?: WorkOrderReportUser
  broker?: WorkOrderReportUser
  pay_rate?: number
  origin_address?: string
  origin_name?: string
  customer?: string
  notes?: string
  workorder_id?: number
  project_id?: number
  weight?: number
  volume?: number
  total_weight?: number
}

export interface WorkOrderReportTagStatusBreakdown {
  new: number
  in_progress: number
  accepted: number
  complete: number
  signed: number
  declined: number
  total: number
}

export interface WorkOrderReport {
  // Work Order Info
  workorder_id: number
  workorder_label: string
  workorder_status: string
  material: string
  pickup_address: string
  dropoff_address: string
  trucks_number: string
  truck_types: string[]
  pay_rate: number
  starting_date: string
  completion_date: string

  // Project Info
  project_id: number
  project_label: string
  project_job_site: string
  project_material: string

  // Assigned People
  subhaulers: WorkOrderReportUser[]
  dispatchers: WorkOrderReportUser[]
  created_by: WorkOrderReportUser

  // Key Metrics
  total_hours_worked: number
  total_number_of_loads: number
  total_weight: number
  completed_tags_count: number

  // Tag Breakdown
  tag_status_breakdown: WorkOrderReportTagStatusBreakdown

  // All Related Tags
  tags: WorkOrderReportTag[]

  // Timeline & Analytics
  earliest_tag_start: string
  latest_tag_completion: string
  average_hours_per_tag: number
  average_loads_per_tag: number
}

// Multi-tag report response from the new API
export interface MultiTagReport {
  // Report metadata
  report_id: string
  label?: string
  generated_at: string
  selected_tag_ids: string[]

  // Summary metrics
  total_tags: number
  total_hours: number
  total_loads: number
  total_weight?: number

  // Tag breakdown
  tag_status_breakdown: WorkOrderReportTagStatusBreakdown

  // All selected tags with full details
  tags: WorkOrderReportTag[]

  // Work orders involved (if multiple)
  work_orders: Array<{
    id: number
    label: string
    status: string
    project_label: string
    subhaulers: WorkOrderReportUser[]
    dispatchers: WorkOrderReportUser[]
  }>

  // Analytics
  average_hours_per_tag: number
  average_loads_per_tag: number
  earliest_tag_start: string
  latest_tag_completion: string
}
